import React, { FC, Fragment } from 'react';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import isNil from 'lodash/isNil';
import { useSelector } from 'react-redux';

import { ITimesheetDashboardEntryDTO } from 'src/api/dtos';
import { useLocalizedJobNames } from 'src/features/jobs';
import { AttachmentIcon, CheckPersonIcon, DollarSignIcon } from 'src/shared/components';
import { DurationFormatEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';
import { formatDepartmentDisplay, formatDuration } from 'src/shared/utils';
import { RootState } from 'src/store/types';

interface Props {
  entry: ITimesheetDashboardEntryDTO;
}

export const QuickEntryItem: FC<Props> = ({ entry }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { settings } = useSelector((state: RootState) => state.user);
  const numberOfJobLevels = settings?.numberOfJobLevels;
  const localizedJobNames = useLocalizedJobNames(numberOfJobLevels);

  const { department, jobs, duration, absenceAndOverride, expense } = entry;

  return (
    <Stack
      direction="row"
      gap={1}
      justifyContent={'space-between'}
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: '10px',
        padding: '8px 10px',
        alignItems: 'center',
      }}
    >
      <Box display="grid" gridTemplateColumns="1fr 1fr" alignItems="center" columnGap={1} sx={{ alignItems: 'start' }}>
        <ComponentWithPermissions permissions={PermissionsEnums.FEATUREDEPARTMENT_ALLOCATION_FUNCTION}>
          {department && (
            <>
              <Typography fontSize="0.875rem" color="text.secondary" fontWeight={500}>
                {t('timesheets.department')}:
              </Typography>
              <Typography fontSize="0.875rem" fontWeight={500}>
                {!!department && formatDepartmentDisplay(department.description, department.number)}
              </Typography>
            </>
          )}
        </ComponentWithPermissions>
        <ComponentWithPermissions permissions={PermissionsEnums.FEATUREJOB_COSTING_FUNCTION}>
          {jobs.map(
            (job, index) =>
              job && (
                <Fragment key={index}>
                  <Typography fontSize="0.875rem" color="text.secondary" fontWeight={500}>
                    {localizedJobNames[index]}:
                  </Typography>
                  <Typography fontSize="0.875rem" fontWeight={500}>
                    {job.values[0]?.value}
                  </Typography>
                </Fragment>
              ),
          )}
        </ComponentWithPermissions>
      </Box>
      <Stack direction="row" alignItems="center" gap={1} width="fit-content">
        <Stack direction="column" flex={1} alignItems="flex-end" gap={1}>
          {!isNil(duration) && (
            <Typography fontSize="1.25rem" fontWeight={500} lineHeight={1}>
              {formatDuration(duration, DurationFormatEnums.T_24_HOURS, 'seconds')}
            </Typography>
          )}
          {absenceAndOverride && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <CheckPersonIcon size={18} color={theme.palette.text.secondary} />
              <Typography fontSize="0.75rem" color="text.secondary">
                {absenceAndOverride.description}
              </Typography>
            </Stack>
          )}
          {expense && (
            <>
              <Typography fontSize="1.25rem" fontWeight={500} lineHeight={1}>
                {expense.memoAmount.toFixed(2)}
              </Typography>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <DollarSignIcon size={18} color={theme.palette.text.secondary} />
                <Typography fontSize="0.75rem" color="text.secondary">
                  {expense.description}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>

        {expense?.attachment && <AttachmentIcon height={18} width={10} color={theme.palette.primary.main} />}
      </Stack>
    </Stack>
  );
};
